import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import format from 'date-fns/format'
import addDays from 'date-fns/add_days';
import addMonths from "date-fns/add_months";
import startOfWeek from 'date-fns/start_of_week'
import _ from 'lodash'
import { getCategory, getCompany, getTeamByCompany } from './core/cacheService'

function normalizeQuestionRequest(payload: any, fallbackUser: any) {
    if (payload && payload.user) {
        return {
            user: payload.user,
            forceRefresh: !!payload.forceRefresh,
        }
    }

    if (payload && typeof payload === 'object' && payload.forceRefresh !== undefined) {
        return {
            user: fallbackUser,
            forceRefresh: !!payload.forceRefresh,
        }
    }

    return {
        user: payload || fallbackUser,
        forceRefresh: false,
    }
}

function sortQuestionsByLabel(questions: any[]) {
    return _.sortBy(questions, [function (o: any) { return o.question_en; }])
}

function filterQuestionsByStatus(questions: any[], status: string) {
    if (!status || status === 'all') {
        return questions
    }

    return _.filter(questions, (item): any => {
        return item.block && item.block.match(status)
    })
}

function filterQuestionsByText(questions: any[], searchValue: string) {
    if (!searchValue) {
        return questions
    }

    const re = new RegExp(searchValue, 'gi')
    return _.filter(questions, (item): any => {
        if (item.question_en && item.question_en.match(re)) {
            return true
        } else if (item.question && item.question.match(re)) {
            return true
        } else if (item.category_name && item.category_name.match(re)) {
            return true
        }
    })
}

function filterQuestionsByOptions(questions: any[], filterData: any) {
    if (!filterData) {
        return questions
    }

    const questionTypes = filterData.questionstype || []
    const powerSkills = filterData.powerskill || []
    const dimensions = filterData.dimension || []
    const activeFilterCount = [questionTypes, powerSkills, dimensions].filter((value) => value.length > 0).length

    if (activeFilterCount === 0) {
        return questions
    }

    const matches: any[] = []
    questionTypes.forEach((value: string) => {
        matches.push(..._.filter(questions, (item) => item.type_name == value))
    })
    powerSkills.forEach((value: string) => {
        matches.push(..._.filter(questions, (item) => item.power_skill == `${value}`))
    })
    dimensions.forEach((value: string) => {
        matches.push(..._.filter(questions, (item) => item.category_name == value))
    })

    const groupedMatches = _.groupBy(matches, function (question) {
        return question.key
    })

    const filteredQuestions: any[] = []
    for (const key in groupedMatches) {
        if (groupedMatches[key].length === activeFilterCount) {
            filteredQuestions.push(groupedMatches[key][0])
        }
    }

    return filteredQuestions
}

function buildVisibleQuestions(state: any, overrides: any = {}) {
    const selectShow = overrides.selectShow !== undefined ? overrides.selectShow : state.selectShow
    const inputSearch = overrides.inputSearch !== undefined ? overrides.inputSearch : state.inputSearch
    const filterEnabled = overrides.filter !== undefined ? overrides.filter : state.filter
    const filterData = overrides.filterData !== undefined ? overrides.filterData : state.filterData

    const statusScopedQuestions = filterQuestionsByStatus(sortQuestionsByLabel(state.questionsall || []), selectShow)
    const filteredQuestions = filterEnabled
        ? sortQuestionsByLabel(filterQuestionsByOptions(statusScopedQuestions, filterData))
        : statusScopedQuestions
    const visibleQuestions = sortQuestionsByLabel(filterQuestionsByText(filteredQuestions, inputSearch))

    return {
        filteredQuestions,
        visibleQuestions,
    }
}

export default {
    namespaced: true,
    state: {
        questions:[],
        questionssearch: [],
        questionsall:[],
        questionLoading: true,
        inputSearch: '',
        selectShow: '',
        countquestion: [],
        notblock_questions: {
            '-Kgs61AHOnpJ3rfyQr6t':'-Kgs61AHOnpJ3rfyQr6t',
            '-Kgn7oxLPPWlbM_ZDc_h':'-Kgn7oxLPPWlbM_ZDc_h',
            '-L0J5FFsGQYVkfcGujWo':'-L0J5FFsGQYVkfcGujWo',
            '-Kgs549ClADmNQwoNbbG':'-Kgs549ClADmNQwoNbbG',
            '-L1FmpFO-v8IZc_B5EB7':'-L1FmpFO-v8IZc_B5EB7',
            '-L2ycGiBDo3rDGjLNKwW':'-L2ycGiBDo3rDGjLNKwW',
            '-L1QWPtZvax_KlJgpNxF':'-L1QWPtZvax_KlJgpNxF',
            '-L1Qug-PhtzVOR2Dr5q7':'-L1Qug-PhtzVOR2Dr5q7',
            '-L36rxTgLhZTL4mq4SV1':'-L36rxTgLhZTL4mq4SV1',
            '-LtOL_iR1N-ev2b_8Tmg':'-LtOL_iR1N-ev2b_8Tmg',
            // Strategic Alignment Questions
            '-OGSh6nBCPhmZVVcSvO6': '-OGSh6nBCPhmZVVcSvO6',
            '-OGSmPA41e8em6xyRJ-5': '-OGSmPA41e8em6xyRJ-5',
            '-OGSj6iv4BiFfUlwg290': '-OGSj6iv4BiFfUlwg290',
            '-OGSfTQqu71fi7f67Ztm': '-OGSfTQqu71fi7f67Ztm',
            '-OGSi-yigX4U4lSn8z7h': '-OGSi-yigX4U4lSn8z7h',
            '-OGSjePG4WFU8A4YH0iB': '-OGSjePG4WFU8A4YH0iB',
            '-OGSiV8s4nrPMhxK_zAe': '-OGSiV8s4nrPMhxK_zAe',
            '-OGSgDcIZeg9vJ6JTUYJ': '-OGSgDcIZeg9vJ6JTUYJ',
            // NPS/eNPS Questions
            '-NsgSScCliV4ASMmFDOW': '-NsgSScCliV4ASMmFDOW',
            '-L1QT78ajNkp6MpsLkBu': '-L1QT78ajNkp6MpsLkBu',
            '-NsgSS2L0gXXecipoJxX': '-NsgSS2L0gXXecipoJxX',
            '-M4F5P10foOhqehfK3lv': '-M4F5P10foOhqehfK3lv',
            // Additional Provided IDs
            '-O5YGsXMzHQqODyAcj-9': '-O5YGsXMzHQqODyAcj-9',
            '-OIJqQ5xzQIJynttG7vu': '-OIJqQ5xzQIJynttG7vu',
            '-ODAK8jLFqXZ9TXBUPKR': '-ODAK8jLFqXZ9TXBUPKR',
            '-O653B7eNPNIjOujRdyZ': '-O653B7eNPNIjOujRdyZ',
            '-ODAJn9r6BtJtSHpUx46': '-ODAJn9r6BtJtSHpUx46',
            '-O-dlF-iBRzT2ts85pyf': '-O-dlF-iBRzT2ts85pyf',
            '-MdMyavdF-YvuyzW6cA5': '-MdMyavdF-YvuyzW6cA5',
            '-O4yPB84ufi6tut3Oj39': '-O4yPB84ufi6tut3Oj39',
            '-O4yP9oPM3-mo_L6tWaC': '-O4yP9oPM3-mo_L6tWaC',
            '-MdMw8kfpZtxnppGUA1f': '-MdMw8kfpZtxnppGUA1f',
            '-O4yP8QHbcJnlV5It92T': '-O4yP8QHbcJnlV5It92T',
            '-MdMv_XgThL0o2iunlhE': '-MdMv_XgThL0o2iunlhE',
            '-MdMz-TodEXbj3AC68oP': '-MdMz-TodEXbj3AC68oP',
            '-O4yP6Pn2Z_gZwHf_nVl': '-O4yP6Pn2Z_gZwHf_nVl',
            '-MdMzwfySVTjhOAGoxTP': '-MdMzwfySVTjhOAGoxTP',
            '-O4yPCW9zzzwnnHwv_B3': '-O4yPCW9zzzwnnHwv_B3'
        },
        category: [],
        scheduleonetime: [],
        page_ot:1,
        size_page_ot:6,
        dateoptions:[],
        dataopenend:[],
        datamultichoices:[],
        organize:[],
        countmultichoices:0,
        loading:true,
        loadingCalendar:true,
        loadingFeedbackAI:true,
        loadingRowHeader: false,
        dateShow:'',
        answerOpt: [],
        questionsOpenendAll:[],
        optionTo:[],
        optionToType:[],
        optionToName:[],
        cohortsUser:[],
        allUsers:[],
        teamArr: [],
        teamAll: [],
        organizations: [],
        organization1level: {},
        level0:[],
        userTeam:{},
        team:[],
        filter:false,
        questionsFilter:[],
        filterData:{},
        dimensionOption:[],
        teamHeight: '',
        datalist: [],
        datalistall: [],
        deletedTeam: [],
        summaryDetail: [],
        dataCompany: {},
        row_header: '',
        questionOrder:{},
        teamleadership:{},
        optionToleadership:{}
    },
    mutations: {
        setQuestions(state:any, result:any) {
            state.questions = result
        },
        setQuestionsSearch(state:any, result:any) {
            state.questionssearch = result
        }, 
        setQuestionLoading(state:any, value:any) {
            state.questionLoading = value;
        }, 
        setQuestionsAll(state: any, result: any) {
            state.questionsall = result
        },
        setinputSearch(state:any,value:any) {
            state.inputSearch = value.value
        },
        setselectShow(state: any, value: any) {
            state.selectShow = value.type
        },
        setCountQuestion(state: any, value: any) {
            state.countquestion = value
        },
        setCategory(state:any, result:any) {
            state.category = result
        },
        setScheduleOnetime(state:any, result:any) {
            state.scheduleonetime = result
        },
        setPageOT(state:any,result:any) {
            state.page_ot = result
        },
        setRemoveLastItem(state:any,result:any) {
            let popped = result.pop();
            state.scheduleonetime = result
        },
        setDateOption(state:any,result:any){
            state.dateoptions = result
        },
        setDataOpenEnd(state:any,result:any){
            state.dataopenend = result
        },
        setDataMultiChoices(state:any,result:any){
            state.datamultichoices = result
        },
        setOrganizeTeam(state:any,result:any){
            state.organize = result
        },
        setCountMultiChoices(state:any,result:number){
            state.countmultichoices = result
        },
        setLoading(state:any,result:boolean){
            state.loading = result
        },
        setLoadingCalendar(state:any,result:boolean){
            state.loadingCalendar = result
        },
        setLoadingRowHeader(state:any,result:boolean){
            state.loadingRowHeader = result
        },
        setLoadingFeedbackAI(state:any,result:boolean){
            state.loadingFeedbackAI = result
        },
        setSummaryDetail(state:any,result:boolean){
            state.summaryDetail = result
        },
        setDateShow(state:any,result:boolean){
            state.dateShow = result
        },
        setAnswerOption(state:any,result:any){
            state.answerOpt = result
        },
        setOptionTo(state:any,result:any){
            state.optionTo = result
        },
        setOptionToType(state:any,result:any){
            state.optionToType = result
        },
        setOptionToName(state:any,result:any){
            state.optionToName = result
        },
        setCohortsUser(state:any,result:any){
            state.cohortsUser = result
        },
        setAllUsers(state:any,result:any){
            state.allUsers = result
        },
        setUserTeam(state:any,result:any){
            state.userTeam = result
        },
        setOrganizations(state: any, value: any) {
            state.organizations = value
        },
        setTeamArr(state:any, result:any) {
            state.teamArr = result
        },
        setTeamAll(state:any, result:any) {
            state.teamAll = result
        },
        setOrganization1level(state:any, result:any) {
            state.organization1level = result
        },
        setFilter(state:any, result:any) {
            state.filter = result
        },
        setQuestionsFilter(state:any, result:any) {
            state.questionsFilter = result
        },
        setFilterData(state:any, result:any) {
            state.filterData = result
        },
        setDimensionOption(state:any, result:any) {
            state.dimensionOption = result
        },
        setTeamHeight(state: any, value: any) {
            state.teamHeight = value
        },
        setDataTable(state: any, value: any) {
            state.datalist = value
        },
        setDataTableAll(state: any, value: any) {
            state.datalistall = value
        },
        setDeleteTeam(state: any, value: any) {
            state.deletedTeam = value
        },
        setDataCompany(state: any, value: any) {
            state.dataCompany = value
        },
        setRowHeader(state: any, value: any) {
            state.row_header = value
        },
        setquestionOrder(state:any, result:any) {
            state.questionOrder=result
        },
        setteamleadership(state:any, result:any) {
            state.teamleadership=result
        },
        setOptionToleadership(state:any, result:any) {
            state.optionToleadership=result
        },
    },
    actions: {
        async getTeamHeight({commit,state,rootState}:any){
            const teams = await getTeamByCompany(rootState.authen.user.company)//(await firebase.database().ref('team').orderByChild("company").equalTo(`${rootState.authen.user.company}`).once('value')).val()
            if(!state.teamHeight){
                let teamHeight:any
                let topmostArr: Array<any> = [];
                var tempData = {};
                for(const x in teams){
                    teams.key = x
                    tempData[teams.key] = teams
                    if(!teams[x].parent){
                        topmostArr.push(x)
                    }
                }
                teamHeight = topmostArr[0]
                if(topmostArr.length > 1){
                    teamHeight = _.find(topmostArr, (value) => {
                    const chk_topmost = _.find(teams, { parent: value });
                    return !!chk_topmost;
                    });
                }
                commit('setTeamArr',teams)
                commit('setTeamHeight',teamHeight)
            }
        },
        async deepSearch({ commit, state, dispatch, rootState }: any, data: any) {
            const organizations = state.organizations
            const organization1level=state.organization1level[state.teamHeight]
                //console.log('organization1level',organization1level)
                const teams = organizations[data.team]
                const response = _.filter(state.datalistall, { 'id': data.team })
                if (teams !== undefined) {
                        response[0]['children'] = []
                
                    if (data.type == 'parent'){
                        response[0]['depth'] = 0
                        response[0]['parent_id'] = null
                        response[0]['extended'] = 'extended'
                    }
                    let i = 1
                    _.forEach(teams.parent, function (value) {
                        if (value.team_id) {
                            const _tmp = _.filter(state.datalistall, { 'id': value.team_id })
                            const ckchild = value.parent
                            _tmp[0]['parent_id'] = data.team
                            _tmp[0]['depth'] = response[0]['depth'] + 1  // Increment depth for children
                            if (ckchild !== undefined) {
                                _tmp[0]['child_num'] = i - 1
                                // setTimeout(() => {
                                    dispatch('deepSearch', { 
                                        team: value.team_id, 
                                        type: 'children',
                                        parentDepth: _tmp[0]['depth']  // Pass current depth to children
                                    })
                                // }, 100)
                            } else {
                                _tmp[0]['child_num'] = 0
                            }
                            response[0]['child_num'] = i
                            response[0]['children'].push(_tmp[0])
                            i++
                        }
                    });
                    if(data.type == 'parent') {
                        if(Object.keys(data.deletedTeam).length > 0){
                            let i = 1
                            for(const keyId in data.deletedTeam){
                               
                            
                                let ObjData:any = {}
                                ObjData.child_num = 0
                                ObjData.depth = 0
                                ObjData.id = keyId
                                ObjData.parent_id = state.teamHeight
                                ObjData.team = 'Deleted Team '+ i
                                if(rootState.authen.user.user_type !== 'leadership'){
                                    response[0].child_num += 1
                                    response[0].children.push(ObjData)
                                }
                                
                                
                                i++
                            }
                        }
                        // setTimeout(() => {
                           
                        
                            commit('setDeleteTeam',data.deletedTeam)
                           
                            commit('setDataTable', response)
                            commit('setLoading', false)
                        // }, 100)
                    }
                }
        },
        async getTeamTree({commit,state,dispatch,rootState}:any,deletedTeam:any){
            let teamHeight = state.teamHeight
            var promise0 = new Promise(function(resolve, reject) {
                if(state.organizations.length == 0){
                    firebase.firestore().collection('organization').where("company", "==", rootState.authen.user.company).get()
                    .then(function (querySnapshot) {
                      const organizations: Array<any> = [];
                      querySnapshot.forEach((doc) => {
                        const organization = doc.data()
                        organization.key = doc.id
                        organizations[organization.team_id] = organization
                      });
                      commit('setOrganizations', organizations)
                      resolve(organizations);
                    });
                  }else{
                    resolve(state.organizations);
                  }
            });
            var promise1 = new Promise(async function(resolve, reject) {
                if(state.teamAll.length == 0){
                    let organization1level  = {}
                    if(rootState.authen.user.user_type == 'leadership'){
                        if(rootState.authen.user.email){
                            const emailKey = rootState.authen.user.email.replace(/\./g, '||')
                            var userData = rootState.main.allUser[emailKey]//(await firebase.database().ref(`/users/${emailKey}`).once('value')).val()
                        }
                        if(userData){
                             organization1level  = state.organization1level[userData.team]?state.organization1level[userData.team]:userData.team
                            // firebase.firestore().collection('organization1level').where("company", "==", userData.company).where("team_id","==",userData.team).get()
                            // .then((querySnapshot)=> {
                            //     if(querySnapshot.empty){
                            //         organization1level[userData.team] = true
                            //     }else{
                            //         querySnapshot.forEach((doc)=> {
                            //             _.forEach(doc.data().parent, function(_,key) {
                            //                 organization1level[key] = true
                            //             });
                            //         });
                            //     }
                            //     //commit('setOrganization1level', organization1level)
                            // });
                        }
                    }
                    
                    const teams2: Array<any> = [];
                    try{
                        const teams = await getTeamByCompany(rootState.authen.user.company)
                        for(const key in teams){
                            const team = teams[key]
                            team.key = key
                            if(rootState.authen.user.user_type == 'leadership'){
                                teamHeight=rootState.authen.user.team
                        
                                if(organization1level[team.key]){
                                
                                    teams2.push(team)
                                }
                            }else{
                                teams2.push(team)
                            }
                        }
                        commit('setTeamAll',teams2)
                        
                        resolve(teams2);
                    }catch(error){
                    }

                   
                    // firebase.database().ref('team').orderByChild('company').equalTo(`${rootState.authen.user.company}`).once('value', snapshot => {
                    //     snapshot.forEach((doc): any => {
                    //     const team = doc.val()
                    //     team.key = doc.key
                    //     if(rootState.authen.user.user_type == 'leadership'){
                    //         teamHeight=rootState.authen.user.team
                       
                    //         if(organization1level[team.key]){
                               
                    //             teams.push(team)
                    //         }
                    //     }else{
                    //         teams.push(team)
                    //     }
                    //     });
                    //     commit('setTeamAll',teams)
                        
                    //     resolve(teams);
                    // });
                }else{
                    if(rootState.authen.user.user_type == 'leadership'){
                        teamHeight=rootState.authen.user.team
                    }
                    resolve(state.teamAll);
                }
            });
            Promise.all([promise0,promise1]).then((values)=> {
                const organizations:any = values[0]
                const teams:any = values[1]
                if (teams.length > 0){
                    if(state.datalistall.length == 0){
                        const response: Array<any> = [];
                        const data: { [key: string]: {} } = {};
                        for (let i = 0; i < teams.length; i++) {
                            data[teams[i].key] = {
                                id: teams[i].key,
                                team: teams[i].name,
                            }
                            response.push(data[teams[i].key])
                           
                        }
                        commit('setDataTableAll', _.sortBy(response, [function (o: any) { return o.team; }]))
                    }
                    
                    dispatch('deepSearch', { team: teamHeight, type: 'parent', deletedTeam: deletedTeam })
                }
            });
        },
        async getDataOrganize({commit,state,rootState}:any, payload:any){
            const { user = rootState.authen.user, forceRefresh = false } = normalizeQuestionRequest(payload, rootState.authen.user)
            if (!user) {
                return []
            }

            if (!forceRefresh && state.organize.length > 0) {
                return state.organize
            }

            const querySnapshot = await firebase.firestore().collection('organization_mom').where("company", "==", user.company).get()
            const teams: string[] = []
            querySnapshot.forEach((doc)=> {
                teams[doc.data().team] = doc.data().mom;
            });
            commit('setOrganizeTeam', teams)
            return teams
        },
        async getDataMultiChoices({commit,state,dispatch,rootState}:any,{date,quest_id}){
            const teams = state.organize;
            let teamTopMost = state.teamHeight;
            if(rootState.authen.user.user_type == 'leadership'){
                 teamTopMost=rootState.authen.user.team
            }
            const _team=state.organization1level[teamTopMost]
            const question = (await firebase.database().ref(`/questions/${quest_id}`).once('value')).val();
            let firstDate = date
            let lastDate = ''
            await firebase.firestore().collection('company_questions').where('company','==',rootState.authen.user.company).where('quest_id','==',quest_id).where('date','>=',firstDate).orderBy('date','asc').limit(2).get().then((querySnapshot)=>{
                querySnapshot.forEach((doc) => {
                    lastDate = doc.data().date
                });
                let citiesRef = firebase.firestore().collection('users_questions').where('company','==',rootState.authen.user.company).where('quest_id','==',quest_id).where('status','==','close')
                if(question.rolling !== undefined && question.rolling == true){
                    if(lastDate && firstDate !== lastDate){
                        citiesRef = citiesRef.where('date', '>=', firstDate).where('date', '<', lastDate)
                    }else{
                        citiesRef = citiesRef.where('date', '>=', firstDate)
                    }
                }else{
                    citiesRef = citiesRef.where('date', '==', firstDate)
                }
                citiesRef.get().then((querySnapshot)=>{
                    const messages : number[][] = []
                    const deletedTeam:Array<any> = []
                    let i = 0;
                    querySnapshot.forEach((doc) => {
                        if (doc.data().answer !== undefined && doc.data().team) {
                            if(rootState.authen.user.user_type == 'leadership'){
                                if(_team[doc.data().team]){
                                    i++;
                                }
                            }else{
                                i++;
                            }
                            
                            if(messages[doc.data().team] === undefined){
                                messages[doc.data().team] = []
                                messages[doc.data().team][0] = 0;
                                messages[doc.data().team][1] = 0;
                            }
                            messages[doc.data().team][doc.data().answer]++
    
                            if(teams[doc.data().team]){
                                for(const x in teams[doc.data().team]){
                                    const team = teams[doc.data().team][x];
                                    if(messages[team] === undefined){
                                        messages[team] = []
                                        messages[team][0] = 0;
                                        messages[team][1] = 0;
                                    }
                                    messages[team][doc.data().answer]++
                                }
                            }else{
                                if(teamTopMost !== doc.data().team){
                                    deletedTeam[doc.data().team] = true
                                    if(messages[teamTopMost] === undefined){
                                        messages[teamTopMost] = []
                                        messages[teamTopMost][0] = 0;
                                        messages[teamTopMost][1] = 0;
                                    }
                                    messages[teamTopMost][doc.data().answer]++
                                }
                            }
                        }
                    });
                    // commit('setLoading', false)
                   
                    commit('setDataMultiChoices', messages)
                    commit('setCountMultiChoices', i)
                    dispatch('getTeamTree', deletedTeam)
                })
            });
        },
        async getDataScale({commit,state,dispatch,rootState}:any,{date,quest_id}) {
            const teams = state.organize;
            
            let teamTopMost = state.teamHeight;
            if(rootState.authen.user.user_type == 'leadership'){
                 teamTopMost=rootState.authen.user.team
            }
            const _team=state.organization1level[teamTopMost]
            const question = (await firebase.database().ref(`/questions/${quest_id}`).once('value')).val();
            let firstDate = date
            let lastDate = ''
            await firebase.firestore().collection('company_questions').where('company','==',rootState.authen.user.company).where('quest_id','==',quest_id).where('date','>=',firstDate).orderBy('date','asc').limit(2).get().then((querySnapshot)=>{
                querySnapshot.forEach((doc) => {
                    lastDate = doc.data().date
                });
                let citiesRef = firebase.firestore().collection('users_questions').where('company','==',rootState.authen.user.company).where('quest_id','==',quest_id).where('status','==','close')
                if(question.rolling !== undefined && question.rolling == true){
                    if(lastDate && firstDate !== lastDate){
                        citiesRef = citiesRef.where('date', '>=', firstDate).where('date', '<', lastDate)
                    }else{
                        citiesRef = citiesRef.where('date', '>=', firstDate)
                    }
                }else{
                    citiesRef = citiesRef.where('date', '==', firstDate)
                }
                citiesRef.get().then((querySnapshot)=>{
                    const messages : number[][] = []
                    const scale : number[][] = []
                    const deletedTeam:Array<any> = []
                    let i = 0;
                    querySnapshot.forEach((doc) => {
                        if (doc.data().answer !== undefined && doc.data().team) {
                            if(rootState.authen.user.user_type == 'leadership'){
                                if(_team[doc.data().team]){
                                    i++;
                                }
                            }else{
                                i++;
                            }
                            if(messages[doc.data().team] === undefined){
                                messages[doc.data().team] = []
                            }
                            if(messages[doc.data().team][doc.data().answer] === undefined){
                                messages[doc.data().team][doc.data().answer] = 0
                            }
                            messages[doc.data().team][doc.data().answer]++
    
                            if(teams[doc.data().team]){
                                for(const x in teams[doc.data().team]){
                                    const team = teams[doc.data().team][x];
                                    if(messages[team] === undefined){
                                        messages[team] = []
                                    }
                                    if(messages[team][doc.data().answer] === undefined){
                                        messages[team][doc.data().answer] = 0
                                    }
                                    messages[team][doc.data().answer]++
                                }
                            }else{
                                if(teamTopMost !== doc.data().team){
                                    deletedTeam[doc.data().team] = true
                                    if(messages[teamTopMost] === undefined){
                                        messages[teamTopMost] = []
                                    }
                                    if(messages[teamTopMost][doc.data().answer] === undefined){
                                        messages[teamTopMost][doc.data().answer] = 0
                                    }
                                    messages[teamTopMost][doc.data().answer]++
                                }
                            }
                        }
                    });
                    for(const x in messages) {
                        if(scale[x] === undefined){
                            scale[x] = []
                        }
                        let no = 0
                        for(const y in messages[x]){
                            if(scale[x]['total'] === undefined){
                                scale[x]['total'] = 0
                                scale[x]['range'] = new Array()
                                scale[x]['range'][0] = 0
                                scale[x]['range'][1] = 0
                                scale[x]['range'][2] = 0
                            }
                            if(Number(y) >= 0 && Number(y) < 7){
                                scale[x]['range'][0] += Number(messages[x][y])
                            }else if(Number(y) > 6 && Number(y) < 9){
                                scale[x]['range'][1] += Number(messages[x][y])
                            }else if(Number(y) > 8){
                                scale[x]['range'][2] += Number(messages[x][y])
                            }
                            scale[x]['total'] += (Number(y) * Number(messages[x][y]))
                            no += Number(messages[x][y])
                        }
                        scale[x]['avg'] = scale[x]['total'] / no > 0 ? Number((scale[x]['total'] / no).toFixed(1)) : 0
                        
                    }
                    // commit('setLoading', false)
                    commit('setDataMultiChoices', scale)
                    commit('setCountMultiChoices', i)
                    dispatch('getTeamTree', deletedTeam)
                })
            });
        },
        async getDataMultiChoicesOther ({commit,state,dispatch,rootState}:any,{date,quest_id}){
            const teams = state.organize;
            let teamTopMost = state.teamHeight;
            if(rootState.authen.user.user_type == 'leadership'){
                 teamTopMost=rootState.authen.user.team
            }
            const _team=state.organization1level[teamTopMost]
            const question = (await firebase.database().ref(`/questions/${quest_id}`).once('value')).val();
            let firstDate = date
            let lastDate = ''
            await firebase.firestore().collection('company_questions').where('company','==',rootState.authen.user.company).where('quest_id','==',quest_id).where('date','>=',firstDate).orderBy('date','asc').limit(2).get().then((querySnapshot)=>{
                querySnapshot.forEach((doc) => {
                    lastDate = doc.data().date
                });
                let citiesRef = firebase.firestore().collection('users_questions').where('company','==',rootState.authen.user.company).where('quest_id','==',quest_id).where('status','==','close')
                if(question.rolling !== undefined && question.rolling == true){
                    if(lastDate && firstDate !== lastDate){
                        citiesRef = citiesRef.where('date', '>=', firstDate).where('date', '<', lastDate)
                    }else{
                        citiesRef = citiesRef.where('date', '>=', firstDate)
                    }
                }else{
                    citiesRef = citiesRef.where('date', '==', firstDate)
                }
                citiesRef.get().then((querySnapshot)=>{
                    const messages : number[][] = []
                    const deletedTeam:Array<any> = []
                    let i = 0;
                    querySnapshot.forEach((doc) => {
                        if (doc.data().answer !== undefined && doc.data().team) {
                            if(rootState.authen.user.user_type == 'leadership'){
                                if(_team[doc.data().team]){
                                    i++;
                                }
                            }else{
                                i++;
                            }
                            
                            if(messages[doc.data().team] === undefined){
                                messages[doc.data().team] = []
                                if(question && question.m_choice){
                                    for(const k in question.m_choice.en){
                                        messages[doc.data().team][k] = 0
                                    }
                                }
                            }
                            messages[doc.data().team][doc.data().answer]++
    
                            if(teams[doc.data().team]){
                                for(const x in teams[doc.data().team]){
                                    const team = teams[doc.data().team][x];
                                    if(messages[team] === undefined){
                                        messages[team] = []
                                        if(question && question.m_choice){
                                            for(const k in question.m_choice.en){
                                                messages[team][k] = 0
                                            }
                                        }
                                    }
                                    messages[team][doc.data().answer]++
                                }
                            }else{
                                if(teamTopMost !== doc.data().team){
                                    deletedTeam[doc.data().team] = true
                                    if(messages[teamTopMost] === undefined){
                                        messages[teamTopMost] = []
                                        if(question && question.m_choice){
                                            for(const k in question.m_choice.en){
                                                messages[teamTopMost][k] = 0
                                            }
                                        }
                                    }
                                    messages[teamTopMost][doc.data().answer]++
                                }
    
                            }
                        }
                    });
                    if(question.m_choice.weight[0] === '0' || question.m_choice.weight[0] === 0 || question.m_choice === 0){
                    }else{
                        for(const x in messages) {
                            messages[x] = messages[x].reverse()
                        }
                    }
                    // commit('setLoading', false)
                    commit('setDataMultiChoices', messages)
                    commit('setCountMultiChoices', i)
                    dispatch('getTeamTree', deletedTeam)
                })
            });
        },
        async getDataOpenEnd({commit,state,dispatch,rootState}:any,{date,quest_id}){
            const organization1level=state.organization1level[rootState.authen.user.team]
            const deletedTeam:Array<any> = []
            const messages : any[] = []
            const messagesLast : string[] = []
            let text = ''
            commit('setLoadingFeedbackAI', true)
            commit('setSummaryDetail', [])
            // const question = (await firebase.database().ref(`/questions/${quest_id}`).once('value')).val();
            let dateLast = format(addMonths(date, -3), 'YYYY-MM-DD')
            // const model = (await firebase.database().ref(`/model/daily_report_feedback`).once('value')).val();
            const materlanguage=(await firebase.database().ref(`/language`).once('value')).val()
            const companyData=await getCompany(rootState.authen.user.company)
            let languages = 'English'
            if(companyData && companyData.language && materlanguage[companyData.language]){
                languages = materlanguage[companyData.language].name
            }
            var promise0 = new Promise(async function(resolve, reject) {
                const question = (await firebase.database().ref(`/questions/${quest_id}`).once('value')).val();
                let firstDate = date
                let lastDate = ''
                await firebase.firestore().collection('company_questions').where('company','==',rootState.authen.user.company).where('quest_id','==',quest_id).where('date','>=',firstDate).orderBy('date','asc').limit(2).get().then((querySnapshot)=>{
                    querySnapshot.forEach((doc) => {
                        lastDate = doc.data().date
                    });
                    let citiesRef = firebase.firestore().collection('users_questions').where('company','==',rootState.authen.user.company).where('quest_id','==',quest_id).where('status','==','close')
                    if(question.rolling !== undefined && question.rolling == true){
                        if(lastDate && firstDate !== lastDate){
                            citiesRef = citiesRef.where('date', '>=', firstDate).where('date', '<', lastDate)
                        }else{
                            citiesRef = citiesRef.where('date', '>=', firstDate)
                        }
                    }else{
                        citiesRef = citiesRef.where('date', '==', firstDate)
                    }
                    citiesRef.get().then((querySnapshot)=>{
                        querySnapshot.forEach((doc) => {
                            if(doc.data().answer !== undefined) {
                                if(rootState.authen.user.user_type == 'leadership'){
                                    if(organization1level[doc.data().team]){
                                        //console.log('doc.data().user',doc.data().user)
                                        // Store both answer and team information
                                        messages.push({
                                            answer: doc.data().answer,
                                            team: doc.data().team,
                                            user: doc.data().users,
                                            date: doc.data().date
                                        })
                                    }
                                }else{
                                    // Store both answer and team information
                                    messages.push({
                                        answer: doc.data().answer,
                                        team: doc.data().team,
                                        user: doc.data().users,
                                        date: doc.data().date
                                    })
                                }
                                
                            }
                        });
                        resolve(messages)
                    })
                });
            });
            var promise1 = new Promise(async function(resolve, reject) {
                firebase.firestore().collection('feedback_ai_generate/').doc(`${rootState.authen.user.company}-${quest_id}-${date}`).get().then(async (doc)=> {
                    if (doc.exists) {
                            //console.log('เคยGenแล้ว')
                        const _data:any = doc.data()
                        if(_data.createdate < (_data.date + ' 23:59:59')){
                            // console.log('วันที่สร้างยังไม่พ้นวันที่ส่งคำถาม')
                            let dataAnswer:any = [];
                            await firebase.firestore().collection('users_questions').where('company','==',rootState.authen.user.company).where('date','>=',dateLast).where('date','<=',date).where('quest_id','==',quest_id).where('status','==','close').get().then(async (querySnapshot)=>{
                                querySnapshot.forEach(async(doc2) => {
                                    const _data2 = doc2.data()
                                    if(_data2.answer !== undefined && _data2.answer.length < 1200) {
                                        messagesLast.push(_data2.answer)
                                        text += `\n${_data2.answer}`
                                        const feedbackText = _data2.answer;
                                        const feedback = await dispatch('summarizeFeedback', feedbackText)
                                        dataAnswer.push(feedback)
                                    }
                                });
                                if (text.length > 16000) {
                                    text = text.slice(0, 16000);
                                }
                                if(_data.response !== messagesLast.length){
                                    var axios = require('axios');
                                    const url=`https://api-node.happily.ai/openai/get_feedback_ai`
                                    // const url = `http://localhost:80/openai/get_feedback_ai`
                                    const config = {
                                        method: 'post',
                                        url: url,
                                        data: {
                                            text: text,
                                            lang: languages
                                        },
                                        headers: {
                                            'Content-Type': 'application/json'
                                        }
                                    };
                                    const response = await axios(config)
                                    const json = response.data;
                                    if(json && json.choices && json.choices[0] && json.choices[0].finish_reason){
                                        if(json.choices[0].finish_reason=="length"){
                                            commit('setLoadingFeedbackAI', false)
                                            resolve([])
                                            console.log("Incomplete model output due to max_tokens parameter or token limit")
                                        } else if(json.choices[0].finish_reason=="content_filter"){
                                            commit('setLoadingFeedbackAI', false)
                                            resolve([])
                                            console.log('Omitted content due to a flag from our content filters')
                                        }else if(json.choices[0].finish_reason==null){
                                            commit('setLoadingFeedbackAI', false)
                                            resolve([])
                                            console.log('API response still in progress or incomplete')
                                        }
                                    }
                                    if(json && json.choices && json.choices[0] && json.choices[0].message && json.choices[0].message.content){
                                        const fixedJsonString =json.choices[0].message.content;
                                        let message;
                                        try {
                                            message = JSON.parse(fixedJsonString);
                                            if(message){
                                                if(dataAnswer.length > 0){
                                                    (async () => {
                                                        try {
                                                            const feedback = dataAnswer
                                                            var axios = require('axios');
                                                            const url=`https://api-node.happily.ai/openai/get_talking_points_ai`
                                                            // const url=`http://localhost:80/openai/get_talking_points_ai`
                                                            const config = {
                                                                method: 'post',
                                                                url: url,
                                                                data: {
                                                                    text: feedback,
                                                                    lang: languages
                                                                },
                                                                headers: {
                                                                    'Content-Type': 'application/json'
                                                                }
                                                            };
                                                            const response_talking_points = await axios(config)
                                                            const json_talking_points = response_talking_points.data;
                                                            const dataObj:any = {
                                                                Summary:message.summary || '',
                                                                company:rootState.authen.user.company,
                                                                quest_id:quest_id,
                                                                date:date,
                                                                response:messagesLast.length,
                                                                prompt: json.prompt,
                                                                prompt_feedback: json_talking_points.prompt,
                                                                createdate:format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                                                                response_talking_points: [],
                                                            }
                                                            
                                                            if(json_talking_points && json_talking_points.choices && json_talking_points.choices[0] && json_talking_points.choices[0].message && json_talking_points.choices[0].message.content){
                                                                const fixedJsonString = json_talking_points.choices[0].message.content;
                                                                let message;
                                                                try {
                                                                    message = JSON.parse(fixedJsonString);
                                                                    if(message && message.talking_points){
                                                                        if(!dataObj.response_talking_points) dataObj.response_talking_points = [];
                                                                        dataObj.response_talking_points = message.talking_points;
                                                                    }else{
                                                                        if(!dataObj.response_talking_points) dataObj.response_talking_points = [];
                                                                        dataObj.response_talking_points.push(message);
                                                                    }
                                                                } catch (error) {
                                                                    message = false;
                                                                }
                                                            }
                                                            if (Object.keys(dataObj).length) {
                                                                // console.log(dataObj)
                                                                firebase.firestore().collection('feedback_ai_generate').doc(`${rootState.authen.user.company}-${quest_id}-${date}`).set(dataObj).then(docRef => {
                                                                    let data:any = [];
                                                                    if(dataObj.response_talking_points){
                                                                        for(const index in dataObj.response_talking_points){
                                                                            let density = Number(dataObj.response_talking_points[index].density)
                                                                            if(density >= 0.3){
                                                                                data.push(dataObj.response_talking_points[index])
                                                                            }
                                                                        }
                                                                    }
                                                                    dataObj.response_talking_points_show = data;
                                                                    messages.unshift(dataObj)
                                                                    commit('setSummaryDetail', dataObj)
                                                                    commit('setLoadingFeedbackAI', false)
                                                                    resolve(dataObj)
                                                                });
                                                            }
                                                        } catch (error) {
                                                            console.error(error);
                                                            commit('setLoadingFeedbackAI', false)
                                                            resolve([])
                                                        }
                                                    })();
                                                }
                                            }
                                            
                                        } catch (error) {
                                            console.log("error",error)
                                            message = false;
                                            commit('setLoadingFeedbackAI', false)
                                            resolve([])
                                        }
                                    }
                                }else{
                                    // console.log('มีอยู่แล้ว เท่าเดิม')
                                    let data:any = [];
                                    if(_data.response_talking_points){
                                        for(const index in _data.response_talking_points){
                                            let density = Number(_data.response_talking_points[index].density)
                                            if(density >= 0.3){
                                                data.push(_data.response_talking_points[index])
                                            }
                                        }
                                    }
                                    _data.response_talking_points_show = data;
                                    messages.unshift(_data)
                                    commit('setSummaryDetail', _data)
                                    commit('setLoadingFeedbackAI', false)
                                    resolve(_data)
                                }
                            });
                        }else{
                            let data:any = [];
                            if(_data.response_talking_points){
                                for(const index in _data.response_talking_points){
                                    let density = Number(_data.response_talking_points[index].density)
                                    if(density >= 0.3){
                                        data.push(_data.response_talking_points[index])
                                    }
                                }
                            }
                            _data.response_talking_points_show = data;
                            messages.unshift(_data)
                            commit('setSummaryDetail', _data)
                            commit('setLoadingFeedbackAI', false)
                            // console.log('วันที่สร้างเกินวันที่ส่งเอกสารไปแล้ว')
                            resolve(_data)
                        }
                    }else{
                            //console.log('ยังไม่เคยGen')
                        let dataAnswer:any = [];
                        await firebase.firestore().collection('users_questions').where('company','==',rootState.authen.user.company).where('date','>=',dateLast).where('date','<=',date).where('quest_id','==',quest_id).where('status','==','close').get().then(async (querySnapshot)=>{
                            querySnapshot.forEach(async (doc2) => {
                                const _data2 = doc2.data()
                                if(_data2.answer !== undefined && _data2.answer.length < 1200) {
                                    messagesLast.push(_data2.answer)
                                    text += `\n${_data2.answer}`
                                    const feedbackText = _data2.answer;
                                    const feedback = await dispatch('summarizeFeedback', feedbackText)
                                    dataAnswer.push(feedback)
                                }
                            });
                            if (text.length > 16000) {
                                text = text.slice(0, 16000);
                            }
                            if(messagesLast.length>0){
                                var axios = require('axios');
                                const url=`https://api-node.happily.ai/openai/get_feedback_ai`
                                // const url = `http://localhost:80/openai/get_feedback_ai`
                                const config = {
                                    method: 'post',
                                    url: url,
                                    data: {
                                        text: text,
                                        lang: languages
                                    },
                                    headers: {
                                        'Content-Type': 'application/json'
                                    }
                                };
                                const response = await axios(config)
                                const json = response.data;
                                if(json && json.choices && json.choices[0] && json.choices[0].finish_reason){
                                    if(json.choices[0].finish_reason=="length"){
                                        commit('setLoadingFeedbackAI', false)
                                        resolve([])
                                        console.log("Incomplete model output due to max_tokens parameter or token limit")
                                    } else if(json.choices[0].finish_reason=="content_filter"){
                                        commit('setLoadingFeedbackAI', false)
                                        resolve([])
                                        console.log('Omitted content due to a flag from our content filters')
                                    }else if(json.choices[0].finish_reason==null){
                                        commit('setLoadingFeedbackAI', false)
                                        resolve([])
                                        console.log('API response still in progress or incomplete')
                                    }

                                }

                                if(json && json.choices && json.choices[0] && json.choices[0].message && json.choices[0].message.content){
                                    const fixedJsonString =json.choices[0].message.content;
                                    let message;
                                    try {
                                        message = JSON.parse(fixedJsonString);
                                        if(message){
                                            if(dataAnswer.length > 0){
                                                (async () => {
                                                    try {
                                                        const feedback = dataAnswer
                                                        var axios = require('axios');
                                                        const url=`https://api-node.happily.ai/openai/get_talking_points_ai`
                                                        // const url=`http://localhost:80/openai/get_talking_points_ai`
                                                        const config = {
                                                            method: 'post',
                                                            url: url,
                                                            data: {
                                                                text: feedback,
                                                                lang: languages
                                                            },
                                                            headers: {
                                                                'Content-Type': 'application/json'
                                                            }
                                                        };
                                                        const response_talking_points = await axios(config)
                                                        const json_talking_points = response_talking_points.data;
                                                        const dataObj:any = {
                                                            Summary:message.summary || '',
                                                            company:rootState.authen.user.company,
                                                            quest_id:quest_id,
                                                            date:date,
                                                            response:messagesLast.length,
                                                            prompt: json.prompt,
                                                            prompt_feedback: json_talking_points.prompt,
                                                            createdate:format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                                                            response_talking_points: [],
                                                        }
                                                        if(json_talking_points && json_talking_points.choices && json_talking_points.choices[0] && json_talking_points.choices[0].message && json_talking_points.choices[0].message.content){
                                                            const fixedJsonString = json_talking_points.choices[0].message.content;
                                                            let message;
                                                            try {
                                                                message = JSON.parse(fixedJsonString);
                                                                if(message && message.talking_points){
                                                                    if(!dataObj.response_talking_points) dataObj.response_talking_points = [];
                                                                    dataObj.response_talking_points = message.talking_points;
                                                                }else{
                                                                    if(!dataObj.response_talking_points) dataObj.response_talking_points = [];
                                                                    dataObj.response_talking_points.push(message);
                                                                }
                                                            } catch (error) {
                                                                message = false;
                                                            }
                                                        }
                                                        if (Object.keys(dataObj).length) {
                                                            // console.log(dataObj)
                                                            firebase.firestore().collection('feedback_ai_generate').doc(`${rootState.authen.user.company}-${quest_id}-${date}`).set(dataObj).then(docRef => {
                                                                let data:any = [];
                                                                if(dataObj.response_talking_points){
                                                                    for(const index in dataObj.response_talking_points){
                                                                        let density = Number(dataObj.response_talking_points[index].density)
                                                                        if(density >= 0.3){
                                                                            data.push(dataObj.response_talking_points[index])
                                                                        }
                                                                    }
                                                                }
                                                                dataObj.response_talking_points_show = data;
                                                                messages.unshift(dataObj)
                                                                commit('setSummaryDetail', dataObj)
                                                                commit('setLoadingFeedbackAI', false)
                                                                resolve(dataObj)
                                                            });
                                                        }
                                                    } catch (error) {
                                                        console.error(error);
                                                        commit('setLoadingFeedbackAI', false)
                                                        resolve([])
                                                    }
                                                })();
                                            }
                                        }
                                        
                                    } catch (error) {
                                        console.log("error",error)
                                        message = false;
                                        commit('setLoadingFeedbackAI', false)
                                        resolve([])
                                    }
                                }
                            }else{
                                // console.log('ไม่มีข้อมูล')
                                commit('setLoadingFeedbackAI', false)
                                resolve([])
                            }
                        })
                    }
                });
            });
            Promise.all([promise0,promise1]).then((values)=> {
                const messages:any = values[0]
                // const summaryDetail:any = values[1]
                //console.log('messages',messages)
                commit('setDataOpenEnd', messages)
                dispatch('getTeamTree', deletedTeam)
            });
        },
        async getDateDetail({commit,dispatch,rootState}:any,{quest_id,type,custom_type,date}){
            const question = (await firebase.database().ref(`/questions/${quest_id}`).once('value')).val();
            firebase.firestore().collection('company_questions').where('company', '==', rootState.authen.user.company).where('quest_id','==',quest_id).get().then( (querySnapshot) => {
                const listdate : Object[] = [];
                let fistdate = '';
                commit('setDateShow', '')
                querySnapshot.forEach((doc) => {
                    if(listdate[doc.data().date] === undefined) {
                        listdate.push({
                            show:format(doc.data().date, 'MMMM D, YYYY'),
                            date:doc.data().date
                        })
                        if(date){
                            commit('setDateShow', format(date, 'MMMM D, YYYY'))
                        }else if(fistdate === '') {
                            commit('setDateShow', format(doc.data().date, 'MMMM D, YYYY'))
                        }
                    }
                });
                if(custom_type == 'multiple_choice'){ // get option answer
                    const selectedLanguage = localStorage.getItem('selectedLanguage') || 'en';
                    let options = question.m_choice.en; // Default to English
                    
                    // Use Thai options if available and selected
                    if (selectedLanguage === 'th' && question.m_choice.th) {
                        options = question.m_choice.th;
                    }
                    commit('setAnswerOption', options)
                    // if(question.m_choice.weight[0] === "0" || question.m_choice.weight[0] === 0){
                    //     commit('setAnswerOption', [...options].reverse())
                    // }else{
                    //     commit('setAnswerOption', options)
                    // }
                }
                const data = _.orderBy(listdate, ['date'], ['desc'])
                
                if(data && data[0] !== undefined && type === 'Multiple Choice'){
                    if(custom_type == 'multiple_choice'){  // Multiple Choice (Other) type
                        if(date){
                            dispatch('getDataMultiChoicesOther', {date:date,quest_id})
                        }else{
                            dispatch('getDataMultiChoicesOther', {date:data[0]['date'],quest_id})
                        }
                    }else{  // Yes/no Type
                        if(date){
                            dispatch('getDataMultiChoices', {date:date,quest_id})
                        }else{
                            dispatch('getDataMultiChoices', {date:data[0]['date'],quest_id})
                        }
                    }
                } else if(data && data[0] !== undefined && type === 'Scale'){
                    if(date){
                        dispatch('getDataScale', {date:date,quest_id})
                    }else{
                        dispatch('getDataScale', {date:data[0]['date'],quest_id})
                    }
                }else if(data && data[0] !== undefined) {
                    if(date){
                        dispatch('getDataOpenEnd', {date:date,quest_id})
                    }else{
                        dispatch('getDataOpenEnd', {date:data[0]['date'],quest_id})
                    }
                }else{
                    commit('setLoading', false)
                }
                
                commit('setDateOption', data)
            });
        },
        async getCategoryOptions({commit,dispatch,state}:any){
            if(state.category.length > 0 && state.dimensionOption.length > 0){
                return state.category
            }

            const categoryData=await getCategory()
            const data:Array<any> = [];
            const dimensionOption:Array<any> = []
            for(const key in categoryData){
                if(categoryData[key].status=="Y"){
                    const _data=categoryData[key]
                    _data.key=key
                    if(!(_data.key == '-KxuZH3ki2YFeNBeNxu0' || _data.key == '-L1uMXKXRgCMnLHXThFK'  || _data.key == '-KxS3N5kloAYbGlYUwXO' || _data.key == '-Lcj5qQhH0T5OwGDiQfM' ) ){
                        data.push(_data)
                        dimensionOption.push({type:_data.category})
                    }
                    
                }
            }
            commit('setDimensionOption', _.sortBy(dimensionOption, [function(o:any) { return o.type; }]))
            commit('setCategory', _.sortBy(data, [function(o:any) { return o.category; }]))
            // firebase.database().ref().child('category').orderByChild('status').equalTo(`Y`).once('value', snapshot => {
            //     const data:Array<any> = [];
            //     const dimensionOption:Array<any> = [];
            //     snapshot.forEach((doc):any => {
            //         const _data = doc.val()
            //         _data.key = doc.key
            //         if(!(_data.key == '-KxuZH3ki2YFeNBeNxu0' || _data.key == '-L1uMXKXRgCMnLHXThFK'  || _data.key == '-KxS3N5kloAYbGlYUwXO' || _data.key == '-Lcj5qQhH0T5OwGDiQfM' ) ){
            //             data.push(_data)
            //             dimensionOption.push({type:_data.category})
            //         }
            //     });
            //     commit('setDimensionOption', _.sortBy(dimensionOption, [function(o:any) { return o.type; }]))
            //     commit('setCategory', _.sortBy(data, [function(o:any) { return o.category; }]))
            // }).catch(error => {
            //     console.log(error)
            // });
            return data
        },
        getScheduleOptions({commit,dispatch,state}:any, obj:any){
            const dateNow = new Date()
            let dayKey:any = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday']
            let dayFind =  0
            if(obj.dayofweek){
                let k = _.findKey(dayKey, item => {
                    return item.toLowerCase() === obj.dayofweek.toLowerCase();
                  });
                if(k) dayFind = parseInt(k)
            }
            let count = 0;
            let day = 0;
            let size = (obj.end_row) ? obj.end_row : state.size_page_ot
            const data:Array<any> = [];
            while ( count < size ){
                const _data:{ [key: string]: string} = {};
                if(startOfWeek(addDays(dateNow, day),{weekStartsOn:dayFind+1}) > dateNow){
                    _data.value = format(startOfWeek(addDays(dateNow, day),{weekStartsOn:dayFind+1}), 'YYYY-MM-DD')
                    _data.label = format(startOfWeek(addDays(dateNow, day),{weekStartsOn:dayFind+1}), 'dddd, MMMM D, YYYY')
                    
                    if(_.findKey(data, ['value', _data.value]) == undefined){
                        count++;
                        data.push(_data)
                    }
                }
                day++
            }
            commit('setScheduleOnetime', data)
        },
        nextOnetimeSchedule({commit, dispatch , state}:any, obj:any){
            var size = state.size_page_ot
            // var start_row = state.page_ot * size
            var end_row = (state.page_ot * size)+size

            commit('setPageOT',state.page_ot+1)
            dispatch('getScheduleOptions', {dayofweek:obj.dayofweek, end_row:end_row})
        },
        async getQuestions({commit,dispatch,state,rootState}:any, payload:any){
            const { user = rootState.authen.user, forceRefresh = false } = normalizeQuestionRequest(payload, rootState.authen.user)
            if (!user) {
                return []
            }

            if (!forceRefresh && state.questionsall.length > 0) {
                const counts: Array<any> = [];
                counts.push(_.countBy(state.questionsall, 'block'))
                counts[0].all = state.questionsall.length
                const { filteredQuestions, visibleQuestions } = buildVisibleQuestions(state)
                commit('setQuestions', visibleQuestions)
                commit('setQuestionsSearch', visibleQuestions)
                commit('setQuestionsFilter', filteredQuestions)
                commit('setCountQuestion', counts[0])
                commit('setQuestionLoading', false)
                return visibleQuestions
            }

            commit('setQuestionLoading', true)
            const category:{ [key: string]: string} = {};
            const company=await getCompany(user.company)//(await firebase.database().ref(`company/${user.company}`).once('value')).val()
            // console.log('company',company)
            const powerupMaster={
                'ps_critical_thinking':{name:'Critical Thinking'},
                'ps_empathy':{name:'Empathy'},
                'ps_initiative_making':{name:'Initiative Making'},
                'ps_leadership':{name:'Leadership'},
                'ps_self_awareness':{name:'Self Awareness'},
                'ps_optimism':{name:'Optimism'}}

            const categoryData=await getCategory()
            for(const key in categoryData){
                const _data=categoryData[key]
                _data.key=key
                if(categoryData[key].status=="Y"){
                    if(_data.key) category[_data.key] = _data.category
                }
            }


            // await firebase.database().ref('category').once('value', snapshot => {
            //     snapshot.forEach((doc):any => {
            //         if(doc.key) category[doc.key] = doc.val().category
            //     }); 
            // });
            const block_questions: { [key: string]: string } = {};
            await firebase.firestore().collection('block_questions').where("company", "==", user.company).get()
            .then(function (querySnapshot) {
                querySnapshot.forEach(function (doc) {
                    if (doc.data().quest_id) block_questions[doc.data().quest_id] = doc.id
                });
            });
            const notblock_questions = state.notblock_questions

            try {
                const snapshot = await firebase.database().ref('questions').orderByChild('company').equalTo(`main`).once('value')
                const questions:Array<any> = [];

                snapshot.forEach((doc):any => {
                    let check_exp=true
                 
                    let start_on=''
                    //console.log('doc.val().starton',doc.val().starton)
                    if(doc.val().starton&&doc.val().starton!="Invalid Date"){
                        const day=doc.val().starton.substring(0, 2);
                        const month=doc.val().starton.substring(3, 5);
                        const year=doc.val().starton.substring(6, 10);
                        start_on=year+'-'+month+"-"+day
                        if(doc.val().repeats=="onetime"&&format(new Date(start_on),"YYYY-MM-DD")<format(new Date(company.createdate),"YYYY-MM-DD")){
                            check_exp=false
                            // console.log('doc.val().question',doc.val().question_en)
                            // console.log('doc.val().starton',format(new Date(start_on),"YYYY-MM-DD"))
                        }
                    }
                    if((doc.key!='-L1u2Xa5_pSCnitgF3u6' && doc.key!= '-L1u2Xa5_pSCnitgF3u65' && doc.key!='-L1u93FCwY42UOoIhXPC' && doc.val().status == 'Y') || (doc.val().parent == 'Y' && doc.val().status == 'N')){
                        const question = doc.val()
                        question.key = doc.key
                        question.category_name = category[doc.val().category]
                        question.block_id = (block_questions[question.key]) ? block_questions[question.key]:''
                        question.block = (block_questions[question.key]) ? 'Y' : 'N'
                        question.block_status = (block_questions[question.key]) ? false : true
                        // Set notblock based *only* on the notblock_questions object from state
                        question.notblock = (notblock_questions[question.key]) ? true : false   

                        
                        if(doc.val().parent == 'Y'){
                            question.type_name = 'Follow-up'
                        }else if(doc.val().type == 'Multiple Choice'){
                            if(doc.val().m_choice.en.length==2)
                            {
                                if(doc.val().m_choice.en[0].toUpperCase()==="YES"&&doc.val().m_choice.en[1].toUpperCase()==="NO")
                                {
                                    question.type_name = 'Yes/No'
                                }
                                else if(doc.val().m_choice.en[1].toUpperCase()==="YES"&&doc.val().m_choice.en[0].toUpperCase()==="NO")
                                {
                                    question.type_name = 'Yes/No'
                                }
                                else{
                                    question.type_name = 'Multiple Choice'
                                    question.custom_type="multiple_choice"}
                            }else{
                                question.type_name = 'Multiple Choice'
                                question.custom_type="multiple_choice"
                            }

                        }else if(doc.val().type == 'Text Area Field'){
                            if(doc.val().power_tag){
                                question.power_skill=`${powerupMaster[doc.val().power_tag].name}`
                            }else{
                                question.power_skill=''
                            }
                            question.type_name = 'Open-ended'
                        }else{
                            question.type_name = doc.val().type
                        }
                        if(doc.val().parent=="Y"){
                            question.frequency="Follow-up"
                        }else if(doc.val().repeats=="onetime"){
                            question.frequency="One-time"
                        }else if(doc.val().repeats=="weekly"){
                            let frequencyDay=0
                            _.forEach(doc.val().repeat_days, (value)=> {
                                if(value==="Y")
                                 {frequencyDay++}
                            });
                            
                            if (frequencyDay >= 5) {
                                question.frequency="Daily"
                              } else {
                                question.frequency="Weekly"
                              }
                            
                        }else if(doc.val().repeats=="monthly"){
                            if (doc.val().repeat_every > 1) {
                                question.frequency="Bi-monthly"
                            }
                            else{
                                question.frequency="Monthly"
                            }
                            
                        }else if(doc.val().repeats=="quarterly"){
                            if (doc.val().repeat_quarters) {
                                if(doc.val().repeat_quarters.Q1=="Y"&&doc.val().repeat_quarters.Q2=="Y"&&doc.val().repeat_quarters.Q3=="Y"&&doc.val().repeat_quarters.Q4=="Y"){
                                    question.frequency="Quarterly"
                                }else{
                                    question.frequency="Bi-annually"
                                }
                            }
                        }else if(doc.val().repeats=="yearly"){
                            question.frequency="Yearly"
                        }
                        // console.log("finsh",question)
                        if(check_exp){questions.push(question)}
                    }
                });
                const sortedQuestions = sortQuestionsByLabel(questions)
                commit('setQuestionsAll', sortedQuestions)
                const counts: Array<any> = [];
                counts.push(_.countBy(sortedQuestions, 'block'))
                counts[0].all = sortedQuestions.length
                const { filteredQuestions, visibleQuestions } = buildVisibleQuestions({
                    ...state,
                    questionsall: sortedQuestions,
                })
                commit('setQuestions', visibleQuestions)
                commit('setQuestionsSearch', visibleQuestions)
                commit('setQuestionsFilter', filteredQuestions)
                commit('setCountQuestion', counts[0])
                return visibleQuestions
            } catch(error) {
                console.log(error)
                return []
            } finally {
                commit('setQuestionLoading', false)
            }
        },
        filtersQuestion({ commit, rootState, dispatch,state}:any,value:any){
          
            commit('setQuestionLoading', true)
            const nextFilterData = {
                questionstype:value.questionstype,
                powerskill:value.powerskill,
                dimension:value.dimension
            }
            const hasFilters = value.powerskill.length > 0 || value.questionstype.length > 0 || value.dimension.length > 0
            commit('setFilter',hasFilters)
            commit('setFilterData',nextFilterData)
            const { filteredQuestions, visibleQuestions } = buildVisibleQuestions(state, {
                filter: hasFilters,
                filterData: nextFilterData,
            })
            commit('setQuestions', visibleQuestions)
            commit('setQuestionsFilter', filteredQuestions)
            commit('setQuestionLoading', false)



        },
        searchQuestions({ commit, rootState, dispatch,state}:any,value:string){
            const { visibleQuestions } = buildVisibleQuestions(state, {
                inputSearch: value,
            })
            commit('setQuestionsSearch', visibleQuestions)
            commit('setQuestions', visibleQuestions)
        },
        selectStatus({ commit, rootState, dispatch, state }: any, value: string) {
            const { visibleQuestions } = buildVisibleQuestions(state, {
                selectShow: value,
            })
            commit('setQuestions', visibleQuestions)
            commit('setQuestionsSearch', visibleQuestions)
        },
        blockQuestion({ commit, rootState, state, dispatch }: any, key: any) {
            firebase.firestore().collection('block_questions').where("company", "==", rootState.authen.user.company).where("quest_id", "==", key).get()
            .then(function (querySnapshot) {
                if(querySnapshot.empty){
                    const block_questions: { [key: string]: string } = {};
                    block_questions['quest_id'] = key
                    block_questions['company'] = rootState.authen.user.company
                    block_questions['email_updated'] = rootState.authen.user.email
                    block_questions['createdate'] = format(new Date(), 'YYYY-MM-DD HH:mm:ss')
                    firebase.firestore().collection('block_questions').add(block_questions).then((docRef) => {
                        /*save logs*/
                        const questions = state.questions;
                        for(const x in questions) {
                            if(questions[x].key === key) {
                                questions[x].block_id = docRef.id;
                                questions[x].block = 'Y';
                                questions[x].block_status = false;
                                break;
                            }
                        }
                        
                        commit('setQuestions', questions)
                        dispatch('authen/addLogs', {
                            company: rootState.authen.user.company,
                            createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                            id: docRef.id,
                            table: 'block_questions',
                            status: 'block',
                            user: rootState.authen.user.email
                        }, {root:true})
                    });
                }else{
                    dispatch('getQuestions',{ user: rootState.authen.user, forceRefresh: true })
                }
            });
        },
        unblockQuestion({ commit, rootState, state, dispatch }: any, key: any) {
            firebase.firestore().collection('block_questions').where("company", "==", rootState.authen.user.company).where("quest_id", "==", key).get()
            .then(function (querySnapshot) {
                querySnapshot.forEach(function (doc) {
                    firebase.firestore().collection('block_questions').doc(doc.id).delete().then(()=>{
                        const questions = state.questions;
                        for(const x in questions) {
                            if(questions[x].key === key) {
                                questions[x].block_id = '';
                                questions[x].block = 'N';
                                questions[x].block_status = true;
                                break;
                            }
                        }
                        commit('setQuestions', questions)
                    });
                    /*save logs*/
                    dispatch('authen/addLogs', {
                        company: rootState.authen.user.company,
                        createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                        id: key,
                        table: 'block_questions',
                        status: 'unblock',
                        user: rootState.authen.user.email
                    }, {root:true})
                });
            });
        },
        async getTeamAndCohort({ commit, rootState, state, dispatch }: any, payload: any){
            const { forceRefresh = false } = normalizeQuestionRequest(payload, rootState.authen.user)
            if (!forceRefresh && state.optionTo.length > 0 && Object.keys(state.optionToName).length > 0) {
                return state.optionTo
            }
            let optionTo:Array<any>=[{ label: "All teams and cohorts",
            value: "all_teams_and_cohorts",type:"all"}]
            const optionToleadership:Array<any>=[]
            const organization=state.organization1level
           
            let optionToType:Array<any> = []
            let optionToName:Array<any> = []
            const cohortsUser:Array<any> = []; 
            const teamAll:Array<any> = []
            const teamArr:{ [key: string]: any } = {}
            optionToType["all_teams_and_cohorts"]="all"
            optionToName["all_teams_and_cohorts"]="All teams and cohorts"
            //console.log('rootState.authen.user',rootState.authen.user)
            const teams = await getTeamByCompany(rootState.authen.user.company)
            for(const key in teams){
                const u = teams[key]
                const team = {
                    ...u,
                    id:key,
                    key:key,
                }
                teamAll.push(team)
                teamArr[key] = team
                if(key){
                  optionToType[key]="team"
                  optionToName[key]=teams[key].name
                }
               
                if(rootState.authen.user.user_type == 'leadership'){
                  // console.log('organization[rootState.authen.user.team]',organization[rootState.authen.user.team])
                  if(organization[rootState.authen.user.team]){
                      for(const i in organization[rootState.authen.user.team]){
                          if(i==key){
                              optionToleadership.push({
                                  label: teams[key].name,
                                  value: key,
                                  type:"team"
                                },)
                          }
                      }

                  }else {   
                      if(rootState.authen.user.team==key){
                          optionToleadership.push({
                              label: teams[key].name,
                              value: key,
                              type:"team"
                          },)
                      }
                  }
                 

                }
                
                
                optionTo.push({
                  label: teams[key].name,
                  value: key,
                  type:"team"
                },)
                
            }
            commit('setOptionToleadership',optionToleadership)
            commit('setTeamArr', teamArr)
            commit('setTeamAll', teamAll)
           
            // firebase.database().ref('team').orderByChild("company").equalTo(`${rootState.authen.user.company}`).once('value', snapshot => {
            //     const teamData:Array<any> = [];
            //     snapshot.forEach((arrData):any => {
            //       const u = arrData.val()
            //       u.id=arrData.key
            //       state.team.push(u)
            //       if(arrData.key){
            //         optionToType[arrData.key]="team"
            //         optionToName[arrData.key]=arrData.val().name
            //       }
            //       if (!arrData.val().parent) {
            //         u.key=arrData.key
            //         state.level0.push(u);
            //       }
                 
            //       if(rootState.authen.user.user_type == 'leadership'){
            //         // console.log('organization[rootState.authen.user.team]',organization[rootState.authen.user.team])
            //         if(organization[rootState.authen.user.team]){
            //             for(const i in organization[rootState.authen.user.team]){
            //                 if(i==arrData.key){
            //                     optionToleadership.push({
            //                         label: arrData.val().name,
            //                         value: arrData.key,
            //                         type:"team"
            //                       },)
            //                 }
            //             }

            //         }else {
            //             if(rootState.authen.user.team==arrData.key){
            //                 optionToleadership.push({
            //                     label: arrData.val().name,
            //                     value: arrData.key,
            //                     type:"team"
            //                 },)
            //             }
            //         }
                   

            //       }
                  
                  
            //       optionTo.push({
            //         label: arrData.val().name,
            //         value: arrData.key,
            //         type:"team"
            //       },)
            //     });
            //     commit('setOptionToleadership',optionToleadership)
            //   }).catch(error => {
            //     console.log(error)
            //   });

            const snapshot = await firebase.database().ref(`cohorts/${rootState.authen.user.company}`).once('value')
            snapshot.forEach((arrData):any => {
              const u = arrData.val()
              if(arrData.key){
                optionToType[arrData.key]="cohorts"
                optionToName[arrData.key]=arrData.val().name
                cohortsUser[arrData.key]={name:arrData.val().name,users:u.users}
              }

              optionTo.push({
                    label: arrData.val().name,
                    value: arrData.key,
                    type:"cohorts"
                  },)
            });

            if(rootState.authen.user.user_type == 'leadership'){
              commit('setteamleadership',rootState.authen.user.team)
            }else {
              commit('setteamleadership','')
            }
            //   console.log('optionToleadership',optionToleadership)
            //   console.log('optionTo',optionTo)
            commit('setCohortsUser',cohortsUser)
            commit('setOptionTo',optionTo)
            commit('setOptionToName',optionToName)
            commit('setOptionToType',optionToType)
            return optionTo
        },
        async getUser({ commit, rootState, state, dispatch}: any, payload: any){
            const { forceRefresh = false } = normalizeQuestionRequest(payload, rootState.authen.user)
            if (!forceRefresh && state.allUsers.length > 0) {
                return state.allUsers
            }
            // Ensure main/getuser has loaded allUser data before reading it
            if (!rootState.main.allUser || Object.keys(rootState.main.allUser).length === 0) {
                await dispatch('main/getuser', false, { root: true })
            }
            
            let users:Array<any>=[]
            for(const arrData in rootState.main.allUser){
                const u = rootState.main.allUser[arrData]
                u.key=arrData
                users.push(u)
            }
            // firebase.database().ref('users').orderByChild("company").equalTo(`${rootState.authen.user.company}`).once('value', snapshot => {
            //     snapshot.forEach((arrData):any => {
            //         const u = arrData.val()
            //         u.key=arrData.key
            //         users.push(u)
                   
            //     })
                let userInTeam= _.groupBy(users, function (b) { return b.team })
                commit('setUserTeam',userInTeam)
                commit('setAllUsers',users)
                return users
            // })
        },
        async getChildren({ commit, rootState, state, dispatch }: any, payload: any){
            const { forceRefresh = false } = normalizeQuestionRequest(payload, rootState.authen.user)
            if (!forceRefresh && Object.keys(state.organization1level).length > 0) {
                return state.organization1level
            }

            const organization1level: { [key: string]: string } = {};
            const querySnapshot = await firebase.firestore().collection('organization1level').where("company", "==", rootState.authen.user.company).get()
            querySnapshot.forEach(function (doc) {
                if(doc.data().team_id){
                    organization1level[doc.data().team_id]=doc.data().parent
                }
            })
            commit('setOrganization1level',organization1level)
            return organization1level
        },
        async getDataCompany({commit,state,rootState}:any, payload:any){
            const { forceRefresh = false } = normalizeQuestionRequest(payload, rootState.authen.user)
            if (!forceRefresh && Object.keys(state.dataCompany).length > 0) {
                return state.dataCompany
            }
            const dataCompany = await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
            commit('setDataCompany', dataCompany)
            return dataCompany
        },
        async generateRowHeader({commit,state,rootState}:any, formData:any){
            commit('setLoadingRowHeader', true)
            if(formData && formData.type && formData.type !== 'Text Area Field' && formData.questions && formData.questions['en'] && formData.questions['en'] !== ''){
                let question = formData.questions['en']
                let choice_options = ''
                if(formData.type === 'Multiple Choice'){
                    choice_options = 'Yes/No'
                }else if(formData.type === 'Multiple Choice(other)'){
                    _.forEach(formData.options, (value,i:any)=> {
                        if(value.en){
                            if(choice_options == ''){
                                choice_options += value.en
                            }else{
                                choice_options += `/${value.en}`
                            }
                        }
                    });
                }else if(formData.type === 'Scale'){
                    if(formData.less_txt_en && formData.max_txt_en){
                        choice_options = `${formData.less_txt_en}/${formData.max_txt_en}`
                    }
                }
                if(choice_options !== ''){
                    var axios = require('axios');
                    const url=`https://api-node.happily.ai/openai/generate_row_header`
                    // const url = `http://localhost:80/openai/generate_row_header`
                    const config = {
                        method: 'post',
                        url: url,
                        data: {
                            question: question,
                            options: choice_options
                        },
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    };
                    const response = await axios(config)
                    const json = response.data;
                    if(json && json.choices && json.choices[0] && json.choices[0].finish_reason){
                        if(json.choices[0].finish_reason=="length"){
                            commit('setRowHeader', '')
                            commit('setLoadingRowHeader', false)
                            console.log("Incomplete model output due to max_tokens parameter or token limit")
                        } else if(json.choices[0].finish_reason=="content_filter"){
                            commit('setRowHeader', '')
                            commit('setLoadingRowHeader', false)
                            console.log('Omitted content due to a flag from our content filters')
                        }else if(json.choices[0].finish_reason==null){
                            commit('setRowHeader', '')
                            commit('setLoadingRowHeader', false)
                            console.log('API response still in progress or incomplete')
                        }

                    }
                    if(json && json.choices && json.choices[0] && json.choices[0].message && json.choices[0].message.content){
                        const fixedJsonString =json.choices[0].message.content;
                        let message;
                        try {
                            message = JSON.parse(fixedJsonString);
                            if(message){
                                commit('setRowHeader', message.statement)
                                commit('setLoadingRowHeader', false)
                            }
                            
                        } catch (error) {
                            console.log("error",error)
                            message = false;
                            commit('setRowHeader', '')
                            commit('setLoadingRowHeader', false)
                        }
                    }
                }else{
                    commit('setRowHeader', '')
                    commit('setLoadingRowHeader', false)
                }
            }else{
                commit('setRowHeader', '')
                commit('setLoadingRowHeader', false)
            }
        },
        async updateCard({commit,state,rootState,dispatch}:any,formData:any){
           // console.log('formData',formData)
            const questions:Array<any> = []
            const date=format(new Date(`${formData.year}-${formData.month}-${formData.day}`), 'YYYY-MM-DD')
            for(const x in formData.data){
                questions.push({order:formData.data[x].order,quest_id:formData.data[x].id,question_en:formData.data[x].question_en})
            }
            const data={
                company:rootState.authen.user.company,
                date:date,
                questions:questions
            }
  
            await firebase.firestore().collection('company_questions_order').doc(`${rootState.authen.user.company}-${date}`).set(data)
        },
        async getQuestionsOrder({commit,state,rootState}:any,payload:any){
            const { forceRefresh = false } = normalizeQuestionRequest(payload, rootState.authen.user)
            if (!forceRefresh && Object.keys(state.questionOrder || {}).length > 0) {
                return state.questionOrder
            }
            const questionOrder={}
            await firebase.firestore().collection('company_questions_order').where("company", "==", `${rootState.authen.user.company}`).get()
            .then(function (querySnapshot) {
                querySnapshot.forEach(function (doc) {
                    const data=doc.data()
                    if(!questionOrder[data.date]){
                        questionOrder[data.date]={}
                    }
                    if(data.questions){
                        for(const i in data.questions){
                            if(!questionOrder[data.date][data.questions[i].quest_id]){
                                questionOrder[data.date][data.questions[i].quest_id]=data.questions[i]
                            }
                        }
                    }
                    
                });
                commit('setquestionOrder', questionOrder)
            });
            return questionOrder
        },
        removeRepeatedCharacters({commit,state,dispatch,rootState}:any,feedback:any) {
            return feedback.replace(/(.)\1{3,}/g, '$1$1$1'); // Replace sequences of 4 or more repeated characters with 3 occurrences
        },
        async summarizeFeedback({commit,state,dispatch,rootState}:any,feedback:any,maxLength=150) {
            // First, remove excessive repeated characters
            feedback = await dispatch('removeRepeatedCharacters', feedback)
            if (feedback.length <= 400) {
                return feedback; // No need to summarize if the length is within acceptable limits
            }
            // Prompt for the summarization task
            var axios = require('axios');
            const url=`https://api-node.happily.ai/openai/summarize_feedback`
            // const url = `http://localhost:80/openai/summarize_feedback`
            const config = {
                method: 'post',
                url: url,
                data: {
                    feedback: feedback,
                    maxLength: maxLength
                },
                headers: {
                    'Content-Type': 'application/json'
                }
            };
            const response = await axios(config)
            const summary = response.data.choices[0].message.content.trim();
            return summary;
          }
    },
    getters: {
        questions : (state:any) => {
            return state.questions
        },
        questionsall : (state:any) => {
            return state.questionsall
        },
        questionLoading : (state:any) => {
            return state.questionLoading
        },
        countquestion: (state: any) => {
            return state.countquestion
        },
        inputSearch: (state:any) => {
            return state.inputSearch
        },
        category: (state:any) => {
            return state.category
        },
        scheduleonetime: (state:any) => {
            return state.scheduleonetime
        },
        dateoptions: (state:any) => {
            return state.dateoptions
        },
        dataopenend:(state:any) => {
            return state.dataopenend
        },
        datamultichoices:(state:any) => {
            return state.datamultichoices
        },
        countmultichoices:(state:any) => {
            return state.countmultichoices
        },
        loading:(state:any) => {
            return state.loading
        },
        loadingCalendar:(state:any) => {
            return state.loadingCalendar
        },
        loadingFeedbackAI:(state:any) => {
            return state.loadingFeedbackAI
        },
        loadingRowHeader:(state:any) => {
            return state.loadingRowHeader
        },
        dateShow:(state:any) => {
            return state.dateShow
        },
        answerOpt:(state:any) => {
            return state.answerOpt
        },
        optionTo:(state:any) => {
            let data=_.orderBy(state.optionTo, ['label'], ['asc']);
            return data//datastate.optionTo
        },
        allUsers:(state:any) => {
            return state.allUsers
        },
        cohortsUser:(state:any) => {
            return state.cohortsUser
        },
        organization1level:(state:any) => {
            return state.organization1level
        },
        userTeam:(state:any) => {
            return state.userTeam
        },
        dimensionOption:(state:any) => {
            return state.dimensionOption
        },
        datalist:(state:any) => {
            return state.datalist
        },
        teamAll:(state:any) => {
            return state.teamAll
        },
        teamArr:(state:any) => {
            return state.teamArr
        },
        organizations: (state:any) => {
            return state.organizations
        },
        deletedTeam: (state:any) => {
            return state.deletedTeam
        },
        teamHeight: (state:any) => {
            return state.teamHeight
        },
        summaryDetail: (state:any) => {
            return state.summaryDetail
        },
        dataCompany: (state:any) => {
            return state.dataCompany
        },
        row_header: (state:any) => {
            return state.row_header
        },
        questionOrder: (state:any) => {
            return state.questionOrder
        },
        teamleadership:(state:any) => {
            return state.teamleadership
        },
        optionToleadership:(state:any) => {
            return state.optionToleadership
        },
    }
  }
